// Defaults for the global scheduling ruleset (Firestore: settings/schedulingRules).
//
// These literals ARE the behaviour that was hardcoded in bookingAvailability.js
// before the config page existed. A missing or partial config doc must
// therefore change nothing — every consumer falls back to this object, and the
// booking tests assert the fallback matches the old rules exactly.
//
// Two pieces:
//
//   `types`     — how one production type contends for time: whole-day or
//                 time-slot, how many fit, and its buffers.
//   `conflicts` — whether a pair of types blocks each other at all, stored
//                 once per unordered pair so the two directions can't drift:
//                   'all'   blocked whoever booked it
//                   'own'   blocked only when it's the same client (you can't
//                           be in two places at once)
//                   'never' not a conflict, even for the same client
//
// A podcast session is a production type of its own, keyed `podcast@<type>`,
// and that is what lets a studio recording hold the room ('all' against
// studio) while leaving the client's field and remote days alone ('never').

import { EVENT_TYPES } from '../utils/calendarEventTypes.js';

// Profile key for a podcast session of a given production type. A calendar
// event is a podcast session when it carries a showId (stamped by
// episodeTemplateService); the override only exists for the types listed in
// `types`, so podcast@field_production falls back to field_production.
export const podcastProfileKey = (type) => `podcast@${type}`;

// Podcast sessions share one crew whatever their production type. It is never
// prefilled at creation: its calendars gate client dates, and once a session
// has a date functions/googleCalendar/podcastCrewTrigger.js assigns whoever is free.
export const PODCAST_CREW_KEY = 'podcast';
export const crewKeyFor = (type) =>
  typeof type === 'string' && type.startsWith('podcast@') ? PODCAST_CREW_KEY : type;

const STUDIO = EVENT_TYPES.ANTHEM_STUDIO;
const FIELD = EVENT_TYPES.FIELD_PRODUCTION;
const REMOTE = EVENT_TYPES.REMOTE_PRODUCTION;
const PODCAST = podcastProfileKey(STUDIO);

// Unordered pair key, so 'a|b' and 'b|a' are the same stored setting.
export const conflictPairKey = (a, b) => [a, b].sort().join('|');

// Phrased as "can these two sit at the same time?", which is how the grid
// reads: the setting is stored once per unordered pair, so there is no
// direction to speak of. A glyph must not fight that — ✓ meaning "blocked"
// reads as its own opposite.
export const CONFLICT_LEVELS = [
  {
    value: 'all',
    glyph: '✕',
    label: 'Never at the same time',
    detail: 'Whoever booked them.'
  },
  {
    value: 'own',
    glyph: '◑',
    label: 'Not for the same client',
    detail: "They can't be in two places at once. Different clients are fine."
  },
  {
    value: 'never',
    glyph: '✓',
    label: 'Fine at the same time',
    detail: 'Even for the same client.'
  }
];

// A pair nobody has configured: a type always clashes with itself, and
// otherwise falls back to the same-client rule. Conservative — a newly added
// type blocks rather than silently double-books.
export const defaultConflictLevel = (a, b) => (a === b ? 'all' : 'own');

const pairs = (entries) => {
  const out = {};
  for (const [a, b, level] of entries) out[conflictPairKey(a, b)] = level;
  return out;
};

export const DEFAULT_SCHEDULING_RULES = {
  types: {
    [FIELD]: {
      conflictMode: 'day',
      capacity: 1,
      bufferMinutes: 0,
      travelBuffer: true,
      // Shown to admins when the day is full. Clients are only ever told a
      // date is unavailable, never why.
      dayConflictReason: 'Another field shoot is already scheduled'
    },
    [REMOTE]: {
      conflictMode: 'time',
      capacity: 1,
      bufferMinutes: 0,
      travelBuffer: false
    },
    [STUDIO]: {
      conflictMode: 'time',
      capacity: 1,
      bufferMinutes: 60,
      travelBuffer: false
    },
    [PODCAST]: {
      conflictMode: 'time',
      capacity: 1,
      bufferMinutes: 60,
      travelBuffer: false
    }
  },

  conflicts: pairs([
    [FIELD, FIELD, 'all'],
    [REMOTE, REMOTE, 'all'],
    [STUDIO, STUDIO, 'all'],
    [PODCAST, PODCAST, 'all'],

    // One studio room: a podcast and a session compete for it either way.
    [STUDIO, PODCAST, 'all'],

    // Different places, so only the same client is stopped.
    [FIELD, REMOTE, 'own'],
    [FIELD, STUDIO, 'own'],
    [REMOTE, STUDIO, 'own'],

    // The podcast exemption: a studio recording leaves this client's field and
    // remote availability alone, and they leave it alone.
    [FIELD, PODCAST, 'never'],
    [REMOTE, PODCAST, 'never']
  ]),

  // Client booking window.
  leadTimeDays: 3,
  workingHours: { startHour: 8, endHour: 17 },
  weekendsBookable: false,

  // Prefilled assignedUserIds per production type, applied at event creation.
  assigneeDefaults: {
    [FIELD]: [],
    [REMOTE]: [],
    [STUDIO]: [],
    podcast: []
  },

  // Gate client date selection on the default assignees' Google Calendars.
  assigneeCalendarCheck: {
    enabled: false,
    types: [],
    horizonDays: 120,
    blockWhenAllBusy: true,
    // An hour. A production day is a whole day: if someone has a real meeting
    // in the middle of it, that day is gone, and a 4-hour bar would wave
    // through a 12-3 commitment that plainly kills a shoot.
    busyThresholdMinutes: 60
  }
};

// Conflict modes a production type can use.
export const CONFLICT_MODES = [
  { value: 'time', label: 'Time slots' },
  { value: 'day', label: 'Whole day' }
];
